import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, CreditCard, 
  FileText, Eye, Edit, Users, Printer, ChevronRight, Globe,
  Clock, Building2, Banknote, ShoppingBag, ExternalLink, Wallet,
  CheckCircle, AlertCircle, TrendingUp
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { api, useAuth } from '../App';
import PaxManager from './PaxManager';
import useConfig, { FALLBACK_STATUS_LABELS } from '../hooks/useConfig';
import { formatCurrency, toNumber, safeFixed } from '../utils/formatters';
import { printPoliceForm } from '../utils/policeFormTemplate';

// Default fallbacks for colors
const DEFAULT_STATUS_COLORS = {
  CONFIRMED: 'bg-blue-100 text-blue-800',
  CHECKED_IN: 'bg-green-100 text-green-800',
  CHECKED_OUT: 'bg-gray-100 text-gray-800',
  FINISHED: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  BLOCKED: 'bg-purple-100 text-purple-800'
};

// Helper to calculate payment status for a reservation
const getPaymentStatus = (reservation) => {
  const total = Number(reservation.total_amount) || 0;
  const deposit = Number(reservation.deposit_amount) || 0;
  const remaining = total - deposit;
  
  if (deposit >= total) return { status: 'paid', label: 'Payé', color: 'text-green-600', bgColor: 'bg-green-50' };
  if (deposit > 0) return { status: 'partial', label: 'Acompte versé', color: 'text-amber-600', bgColor: 'bg-amber-50' };
  return { status: 'pending', label: 'À payer', color: 'text-red-600', bgColor: 'bg-red-50' };
};

export default function GuestProfile({ guest, onBack, onGuestUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPaxDialog, setShowPaxDialog] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Load status configuration
  const { options: configOptions, loading: configLoading } = useConfig(['RESERVATION_STATUS']);
  
  const statusLabels = useMemo(() => {
    const base = { ...FALLBACK_STATUS_LABELS };
    if (!configLoading && configOptions.RESERVATION_STATUS) {
      configOptions.RESERVATION_STATUS.forEach(o => {
        base[o.code] = o.label;
      });
    }
    base.FINISHED = base.CHECKED_OUT || 'Terminée'; // Alias
    return base;
  }, [configLoading, configOptions.RESERVATION_STATUS]);
  
  const statusColors = DEFAULT_STATUS_COLORS;

  // Check if user can see financial info
  const canSeeFinancials = user?.permissions?.includes('finance.view') || 
                           user?.role?.toLowerCase() === 'propriétaire' ||
                           user?.role?.toLowerCase() === 'owner';

  useEffect(() => {
    if (guest?.id) {
      fetchData();
    }
  }, [guest?.id]);

  const fetchData = async () => {
    try {
      const [resResponse, salesResponse] = await Promise.all([
        api.get('/reservations', { params: { guest_id: guest.id } }),
        api.get('/sales', { params: { guest_id: guest.id } })
      ]);
      setReservations(resResponse.data.data || []);
      setSales(salesResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditGuest = () => {
    setEditData({
      first_name: guest.first_name,
      last_name: guest.last_name,
      email: guest.email || '',
      phone: guest.phone || '',
      country: guest.country || '',
      address: guest.address || '',
      notes: guest.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveGuest = async () => {
    try {
      await api.put(`/guests/${guest.id}`, editData);
      toast.success('Client mis à jour');
      setShowEditDialog(false);
      if (onGuestUpdate) {
        onGuestUpdate({ ...guest, ...editData });
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleViewReservation = (reservation) => {
    setSelectedReservation(reservation);
  };

  const handlePrintPolice = async (reservation) => {
    try {
      const response = await api.get(`/reservations/${reservation.id}/police-form`);
      const data = response.data;
      
      // Use centralized template
      printPoliceForm(data);
    } catch (error) {
      toast.error('Erreur lors de la génération de la fiche de police');
    }
  };

  // Stats summary with payment tracking
  const stats = {
    totalStays: reservations.filter(r => r.status !== 'CANCELLED' && r.status !== 'BLOCKED').length,
    totalSpent: reservations.reduce((sum, r) => sum + toNumber(r.total_amount), 0),
    totalNights: reservations.reduce((sum, r) => sum + (r.nights || 0), 0),
    avgStay: reservations.length > 0 
      ? safeFixed(reservations.reduce((sum, r) => sum + (r.nights || 0), 0) / reservations.length, 1)
      : 0,
    totalSales: sales.reduce((sum, s) => sum + toNumber(s.total), 0),
    salesCount: sales.length,
    // Payment tracking
    totalDeposits: reservations.reduce((sum, r) => sum + toNumber(r.deposit_amount), 0),
    pendingPayments: reservations.filter(r => {
      const total = toNumber(r.total_amount);
      const deposit = toNumber(r.deposit_amount);
      return deposit < total && r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT' && r.status !== 'FINISHED';
    }).length,
    // First and last visit
    firstVisit: reservations.length > 0 
      ? reservations.reduce((oldest, r) => new Date(r.check_in) < new Date(oldest.check_in) ? r : oldest, reservations[0])
      : null,
    lastVisit: reservations.length > 0
      ? reservations.reduce((newest, r) => new Date(r.check_in) > new Date(newest.check_in) ? r : newest, reservations[0])
      : null
  };

  // Navigate to reservation detail
  const goToReservation = (reservation) => {
    navigate(`/reservations?id=${reservation.id}`);
  };

  // Navigate to sale
  const goToSale = (sale) => {
    navigate(`/sales?sale_id=${sale.id}`);
  };

  return (
    <div className="space-y-6" data-testid="guest-profile">
      {/* Back Button */}
      <Button variant="ghost" onClick={onBack} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour aux clients
      </Button>

      {/* Guest Header */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-2xl font-bold text-primary">
                {guest.first_name?.[0]}{guest.last_name?.[0]}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{guest.first_name} {guest.last_name}</h1>
                {guest.country && (
                  <div className="flex items-center gap-1 text-muted-foreground mt-1">
                    <Globe className="w-4 h-4" />
                    {guest.country}
                  </div>
                )}
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  {guest.email && (
                    <a href={`mailto:${guest.email}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-primary">
                      <Mail className="w-4 h-4" />
                      {guest.email}
                    </a>
                  )}
                  {guest.phone && (
                    <a href={`tel:${guest.phone}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-primary">
                      <Phone className="w-4 h-4" />
                      {guest.phone}
                    </a>
                  )}
                </div>
                {/* Client depuis */}
                {stats.firstVisit && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Client depuis {format(new Date(stats.firstVisit.check_in), 'MMMM yyyy', { locale: fr })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleEditGuest}>
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-3 mt-6 grid-cols-2 md:grid-cols-5">
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-2xl font-bold text-primary">{stats.totalStays}</p>
              <p className="text-xs text-muted-foreground">Séjours</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-2xl font-bold text-primary">{stats.totalNights}</p>
              <p className="text-xs text-muted-foreground">Nuits</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-2xl font-bold text-primary">{stats.salesCount}</p>
              <p className="text-xs text-muted-foreground">Achats</p>
            </div>
            {canSeeFinancials && (
              <>
                <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalSpent + stats.totalSales)}</p>
                  <p className="text-xs text-muted-foreground">CA généré</p>
                </div>
                {stats.pendingPayments > 0 && (
                  <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
                    <p className="text-2xl font-bold text-amber-600">{stats.pendingPayments}</p>
                    <p className="text-xs text-amber-700">Paiements en attente</p>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Reservations and Sales */}
      <Tabs defaultValue="reservations" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="reservations" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Réservations ({reservations.length})
          </TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            Ventes ({sales.length})
          </TabsTrigger>
        </TabsList>

        {/* Reservations Tab */}
        <TabsContent value="reservations">
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : reservations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune réservation</p>
              ) : (
                <div className="space-y-3">
                  {reservations.map((reservation) => {
                    const paymentInfo = getPaymentStatus(reservation);
                    const total = toNumber(reservation.total_amount);
                    const deposit = toNumber(reservation.deposit_amount);
                    const remaining = total - deposit;
                    
                    return (
                      <div 
                        key={reservation.id}
                        className="border rounded-lg p-4 hover:bg-muted/50 hover:border-primary/30 transition-all cursor-pointer group"
                        onClick={() => goToReservation(reservation)}
                        data-testid={`reservation-${reservation.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div className="flex items-center gap-4">
                            {/* Dates */}
                            <div className="text-center min-w-[70px]">
                              <p className="text-lg font-bold">{format(new Date(reservation.check_in), 'dd MMM', { locale: fr })}</p>
                              <p className="text-xs text-muted-foreground">{format(new Date(reservation.check_in), 'yyyy')}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            <div className="text-center min-w-[70px]">
                              <p className="text-lg font-bold">{format(new Date(reservation.check_out), 'dd MMM', { locale: fr })}</p>
                              <p className="text-xs text-muted-foreground">{format(new Date(reservation.check_out), 'yyyy')}</p>
                            </div>
                            
                            {/* Status & Info */}
                            <div className="pl-4 border-l">
                              <div className="flex items-center gap-2">
                                <Badge className={statusColors[reservation.status]}>
                                  {statusLabels[reservation.status] || reservation.status}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {reservation.source}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {reservation.nights} nuits • {reservation.adults + (reservation.children || 0)} pers.
                                {reservation.pax && reservation.pax.length > 0 && (
                                  <span className="ml-2">
                                    • <span className="text-primary font-medium">{reservation.pax.length} PAX</span>
                                    {reservation.pax.some(p => p.passport_photo) && (
                                      <span className="ml-1 text-green-600" title="Photos passeport disponibles">
                                        ({reservation.pax.filter(p => p.passport_photo).length} photos)
                                      </span>
                                    )}
                                  </span>
                                )}
                                {reservation.police_form_generated_at && (
                                  <Badge variant="outline" className="ml-2 text-xs bg-green-50 text-green-700 border-green-300">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Fiche police
                                  </Badge>
                                )}
                              </p>
                            </div>
                          </div>
                          
                          {/* Financial Info */}
                          <div className="flex items-center gap-4">
                            {canSeeFinancials && (
                              <div className="text-right">
                                <p className="font-bold text-lg">{formatCurrency(total, reservation.currency)}</p>
                                {/* Payment status */}
                                {reservation.source === 'DIRECT' && (
                                  <div className={`text-xs flex items-center justify-end gap-1 ${paymentInfo.color}`}>
                                    {paymentInfo.status === 'paid' ? (
                                      <CheckCircle className="w-3 h-3" />
                                    ) : paymentInfo.status === 'partial' ? (
                                      <Wallet className="w-3 h-3" />
                                    ) : (
                                      <AlertCircle className="w-3 h-3" />
                                    )}
                                    {paymentInfo.label}
                                    {paymentInfo.status === 'partial' && (
                                      <span className="ml-1">({formatCurrency(deposit)})</span>
                                    )}
                                  </div>
                                )}
                                {remaining > 0 && reservation.source === 'DIRECT' && paymentInfo.status !== 'paid' && (
                                  <p className="text-xs text-red-500">Reste: {formatCurrency(remaining)}</p>
                                )}
                              </div>
                            )}
                            
                            {/* Action buttons */}
                            <div className="flex gap-1 opacity-60 group-hover:opacity-100">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); goToReservation(reservation); }}
                                title="Voir la réservation"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); setSelectedReservation(reservation); setShowPaxDialog(true); }}
                                title="Gérer les voyageurs"
                              >
                                <Users className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handlePrintPolice(reservation); }}
                                title="Fiche de police"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales">
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : sales.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune vente</p>
              ) : (
                <div className="space-y-4">
                  {sales.map((sale) => (
                    <div 
                      key={sale.id}
                      className="border rounded-lg overflow-hidden hover:border-primary/30 transition-colors cursor-pointer group"
                      onClick={() => goToSale(sale)}
                      data-testid={`sale-${sale.id}`}
                    >
                      {/* Sale Header */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-muted/30">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <ShoppingBag className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Vente #{sale.sale_number || sale.id}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(sale.sale_date || sale.createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className={sale.status === 'PAID' ? 'text-green-600 border-green-200' : 'text-orange-600 border-orange-200'}>
                            {sale.status === 'PAID' ? 'Payée' : 'En attente'}
                          </Badge>
                          {canSeeFinancials && (
                            <p className="font-bold text-lg text-green-600">{formatCurrency(sale.total)}</p>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="opacity-60 group-hover:opacity-100"
                            onClick={(e) => { e.stopPropagation(); goToSale(sale); }}
                            title="Voir dans les ventes"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                        </div>
                      </div>
                      
                      {/* Sale Lines - Detailed content */}
                      {sale.lines && sale.lines.length > 0 && (
                        <div className="p-4 border-t">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-muted-foreground">
                                <th className="pb-2">Article</th>
                                <th className="pb-2 text-center">Qté</th>
                                {canSeeFinancials && (
                                  <>
                                    <th className="pb-2 text-right">Prix unit.</th>
                                    <th className="pb-2 text-right">Total</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {sale.lines.map((line, idx) => (
                                <tr key={line.id || idx} className="border-t">
                                  <td className="py-2">{line.product_name}</td>
                                  <td className="py-2 text-center">{line.quantity}</td>
                                  {canSeeFinancials && (
                                    <>
                                      <td className="py-2 text-right">{formatCurrency(line.unit_price)}</td>
                                      <td className="py-2 text-right font-medium">{formatCurrency(line.line_total)}</td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                            {canSeeFinancials && (
                              <tfoot>
                                <tr className="border-t font-semibold">
                                  <td colSpan={3} className="pt-2 text-right">Total</td>
                                  <td className="pt-2 text-right text-green-600">{formatCurrency(sale.total)}</td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      )}
                      
                      {/* Show message if no lines */}
                      {(!sale.lines || sale.lines.length === 0) && (
                        <div className="p-4 border-t text-center text-sm text-muted-foreground">
                          Détail non disponible
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notes */}
      {guest.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{guest.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Guest Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le client</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prénom</Label>
              <Input 
                value={editData.first_name || ''} 
                onChange={(e) => setEditData({...editData, first_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Nom</Label>
              <Input 
                value={editData.last_name || ''} 
                onChange={(e) => setEditData({...editData, last_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={editData.email || ''} 
                onChange={(e) => setEditData({...editData, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input 
                value={editData.phone || ''} 
                onChange={(e) => setEditData({...editData, phone: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Pays</Label>
              <Input 
                value={editData.country || ''} 
                onChange={(e) => setEditData({...editData, country: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Adresse</Label>
              <Input 
                value={editData.address || ''} 
                onChange={(e) => setEditData({...editData, address: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea 
                value={editData.notes || ''} 
                onChange={(e) => setEditData({...editData, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveGuest}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pax Manager Dialog */}
      <Dialog open={showPaxDialog} onOpenChange={setShowPaxDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Voyageurs - {selectedReservation && format(new Date(selectedReservation.check_in), 'dd/MM/yyyy')}
            </DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <PaxManager 
              reservationId={selectedReservation.id}
              onClose={() => setShowPaxDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
